import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { tagged } from "@mirage/service-logging";
import { isStagingBuildChannel } from "@mirage/shared/util/tiny-utils";
import { useCallback, useEffect, useState } from "react";
import {
  clearStoredApiEnvironment,
  getApiEnvironment,
  setApiEnvironment,
} from "./";
import { ApiEnvironment } from "./types";

const logger = tagged("useApiEnvironment");

// Helper function to determine the default environment
const determineDefaultEnvironment = (
  shouldUseStageBackendFromGrowthbook: boolean,
  isStaging: boolean,
): ApiEnvironment => {
  return shouldUseStageBackendFromGrowthbook || isStaging
    ? ApiEnvironment.STAGE
    : ApiEnvironment.PROD;
};

export function useApiEnvironment() {
  const [environment, setEnvironment] = useState<ApiEnvironment | null>(null); // Initialize as null

  const shouldUseStageBackendFromGrowthbook = convertFeatureValueToBool(
    useFeatureFlagValue("dash_web_2024_09_17_use_stage_backend"),
  );
  const isStaging = isStagingBuildChannel(EnvCtx.buildChannel);

  useEffect(() => {
    let isMounted = true;
    // Check for stored override first
    getApiEnvironment()
      .then((storedEnv) => {
        if (!isMounted) return;
        if (storedEnv !== null) {
          // Use stored override if it exists and is different from current state
          if (environment !== storedEnv) {
            setEnvironment(storedEnv);
          }
        } else {
          // No override found, determine default based on current flags/channel
          const defaultEnv = determineDefaultEnvironment(
            shouldUseStageBackendFromGrowthbook,
            isStaging,
          );
          // Update state and storage only if the determined default is different
          if (environment !== defaultEnv) {
            setEnvironment(defaultEnv);
            // Store the determined default since no override was present
            setApiEnvironment(defaultEnv).catch((error) => {
              logger.error(
                "Failed to set API environment based on flag/channel",
                error,
              );
            });
          }
        }
        return;
      })
      .catch((error) => {
        if (!isMounted) return;
        logger.error("Failed to get API environment", error);
        // Fallback to default determination on error
        const defaultEnv = determineDefaultEnvironment(
          shouldUseStageBackendFromGrowthbook,
          isStaging,
        );
        if (environment !== defaultEnv) {
          setEnvironment(defaultEnv);
          setApiEnvironment(defaultEnv).catch((err) => {
            logger.error("Failed to set fallback API environment", err);
          });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [shouldUseStageBackendFromGrowthbook, isStaging, environment]);

  const onChange = useCallback(
    (value: ApiEnvironment | null) => {
      if (value) {
        // Set manual override
        setEnvironment(value);
        setApiEnvironment(value).catch((error) => {
          logger.error("Failed to set manual API environment override", error);
        });
      } else {
        // Clear manual override and fall back to default
        clearStoredApiEnvironment()
          .then(() => {
            const defaultEnv = determineDefaultEnvironment(
              shouldUseStageBackendFromGrowthbook,
              isStaging,
            );
            setEnvironment(defaultEnv);
            // Store the default environment after clearing the override
            return setApiEnvironment(defaultEnv);
          })
          .catch((error) => {
            logger.error("Failed to clear and reset API environment", error);
            // Attempt to set default even if clearing failed
            const defaultEnv = determineDefaultEnvironment(
              shouldUseStageBackendFromGrowthbook,
              isStaging,
            );
            setEnvironment(defaultEnv);
          });
      }
    },
    [shouldUseStageBackendFromGrowthbook, isStaging],
  );

  return [environment, onChange] as const;
}
